/**
 * CategoryPage.js - Enhanced category page component with improved error handling
 * 
 * This component displays a category and its channels with enhanced error handling,
 * retry logic, and improved fallback experience.
 */
import React, { useCallback, Suspense, useState, useRef, useEffect } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import ChannelCard from './ChannelCard';
import ThumbnailHoverModal from './ThumbnailHoverModal';
import PDPModal from './PDPModal';
import { useSimpleCategoryDetails } from '../hooks/useSimpleCategoryDetails';
import LoadingSpinner from './common/LoadingSpinner';
import ErrorBoundary from './common/ErrorBoundary';
import Header from './common/Header';
import Footer from './common/Footer';
import './CategoryPage.css';

const CategoryPage = ({ categorySlug, onBack }) => {
  // Get the category slug from URL params (which might actually be an ID)
  const { categorySlug: urlParam } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Redirect to homepage if channel query parameter is present
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const channelSlug = searchParams.get('channel');

    if (channelSlug) {
      // Redirect to homepage with the channel parameter
      navigate(`/?channel=${channelSlug}`, { replace: true });
    }
  }, [location.search, navigate]);
  
  // Determine the actual identifier and whether it's a slug or ID
  const identifier = urlParam || categorySlug;
  
  // MongoDB ObjectId pattern: 24 hex characters
  const isMongoId = /^[0-9a-fA-F]{24}$/.test(identifier);
  const isSlug = !isMongoId;
  
  // Use the simplified hook that actually works
  const { 
    category, 
    channels, 
    loading, 
    error, 
    retry 
  } = useSimpleCategoryDetails(identifier, isSlug);

  // State for hover modal
  const [hoverModalData, setHoverModalData] = useState({
    isVisible: false,
    channel: null,
    position: { x: 0, y: 0 },
  });
  
  // Track which card is currently hovered
  const [hoveredCardIndex, setHoveredCardIndex] = useState(-1);

  // State for PDP modal
  const [pdpModalData, setPdpModalData] = useState({
    isVisible: false,
    channel: null,
    videoState: { autoPlay: false, currentTime: 0 },
  });

  // Ref for hover timeout
  const hoverTimeoutRef = useRef(null);

  // Memoized event handlers
  const handleBackClick = useCallback(() => {
    if (onBack && typeof onBack === 'function') {
      onBack();
    } else {
      // Use browser's back navigation if no onBack prop provided
      navigate(-1);
    }
  }, [onBack, navigate]);

  // Handle hover modal show/hide with proper timeout management
  const handleHoverModalShow = useCallback((channel, position, index) => {
    // If we're already showing a modal for a different card, hide it first
    if (hoverModalData.isVisible && hoveredCardIndex !== index) {
      // Hide current modal
      setHoverModalData(prev => ({
        ...prev,
        isVisible: false,
      }));
      
      // Reset current displacement
      resetNeighborDisplacement();
      
      // Short delay before showing the new modal to allow animation to complete
      setTimeout(() => {
        showNewModal(channel, position, index);
      }, 250); // Wait for exit animation to complete
    } else {
      // No modal showing or same card, show immediately
      showNewModal(channel, position, index);
    }
  }, [hoverModalData.isVisible, hoveredCardIndex]);
  
  // Helper function to show a new modal
  const showNewModal = useCallback((channel, position, index) => {
    // Clear any pending hide timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    
    // Add scroll offset to position for proper positioning when scrolled
    const adjustedPosition = {
      x: position.x,
      y: position.y + (window.scrollY || window.pageYOffset || 0)
    };
    
    // Add a small delay before showing to prevent immediate flickering
    hoverTimeoutRef.current = setTimeout(() => {
      setHoverModalData({
        isVisible: true,
        channel,
        position: adjustedPosition,
      });
      
      // Store the hovered card index and apply displacement
      setHoveredCardIndex(index);
      applyNeighborDisplacement(index);
      
    }, 200); // 200ms delay before showing
  }, []);

  const handleHoverModalHide = useCallback(() => {
    // Clear any existing timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    // Set a timeout to hide the modal after a delay
    hoverTimeoutRef.current = setTimeout(() => {
      setHoverModalData(prev => ({
        ...prev,
        isVisible: false,
      }));
      
      // Reset neighbor displacement and hovered index
      resetNeighborDisplacement();
      setHoveredCardIndex(-1);
      
    }, 300); // 300ms delay before hiding
  }, []);

  const handleModalMouseEnter = useCallback(() => {
    // Cancel hide timeout when mouse enters modal
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  }, []);

  const handleModalMouseLeave = useCallback(() => {
    // Start hide timeout when mouse leaves modal
    handleHoverModalHide();
  }, [handleHoverModalHide]);

  const handleOpenPDPModal = useCallback(videoState => {
    // Hide the hover modal first
    setHoverModalData(prev => ({
      ...prev,
      isVisible: false,
    }));

    // Open the PDP modal with the current channel
    setPdpModalData({
      isVisible: true,
      channel: hoverModalData.channel,
      videoState: videoState || { autoPlay: false, currentTime: 0 },
    });
  }, [hoverModalData.channel]);

  const handleClosePDPModal = useCallback(() => {
    setPdpModalData({
      isVisible: false,
      channel: null,
      videoState: { autoPlay: false, currentTime: 0 },
    });
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);
  
  // Add neighbor displacement functions
  const applyNeighborDisplacement = (hoveredIndex) => {
    // Get all cards in the category grid
    const cards = document.querySelectorAll('.category-page .channel-card-container');
    
    cards.forEach((card, index) => {
      if (index === hoveredIndex) return;
      
      const distance = index - hoveredIndex;
      const direction = distance > 0 ? 1 : -1;
      const absDistance = Math.abs(distance);
      
      let displacement = 0;
      
      // Cards immediately adjacent get pushed more
      if (absDistance === 1) {
        displacement = direction * 60; // 60px displacement
      }
      // Cards two positions away get pushed less
      else if (absDistance === 2) {
        displacement = direction * 30; // 30px displacement
      }
      
      // Apply transform with transition
      card.style.transform = displacement ? `translateX(${displacement}px)` : '';
      card.style.transition = 'transform 0.3s ease';
    });
  };

  const resetNeighborDisplacement = () => {
    // Get all cards in the category grid
    const cards = document.querySelectorAll('.category-page .channel-card-container');
    
    // Reset all transforms
    cards.forEach(card => {
      card.style.transform = '';
    });
  };

  if (loading) {
    return (
      <div className="category-page bg-page min-h-screen">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center mb-6">
            <button 
              onClick={handleBackClick}
              className="text-white flex items-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
              </svg>
              Back
            </button>
          </div>
          
          {/* Enhanced skeleton loading state with more variety */}
          <div className="animate-pulse">
            <div className="h-8 bg-gray-700 rounded w-48 mb-8"></div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 13, 14, 15].map(i => (
                <div key={i} className="flex flex-col">
                  {/* Match ChannelCard image dimensions - using aspect-video for 16:9 aspect ratio */}
                  <div className={`w-full aspect-video bg-gray-700 rounded-lg ${i % 3 === 0 ? 'opacity-90' : i % 3 === 1 ? 'opacity-70' : 'opacity-80'}`}></div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error && (!category || !channels.length)) {
    return (
      <div className="category-page bg-page min-h-screen">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center mb-6">
            <button 
              onClick={handleBackClick}
              className="text-white flex items-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
              </svg>
              Back
            </button>
          </div>
          
          {/* Enhanced error display with more helpful information */}
          <div className="bg-red-900/30 border border-red-500 rounded-lg p-6 text-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-red-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2 className="text-xl font-bold text-white mb-2">Error Loading Category</h2>
            <p className="text-white/70 mb-4">{error}</p>
            <div className="flex justify-center space-x-4">
              <button 
                onClick={handleBackClick}
                className="bg-accent hover:bg-accent/80 text-white font-medium py-2 px-6 rounded-full"
              >
                Go Back
              </button>
              <button 
                onClick={retry}
                className="bg-white/10 hover:bg-white/20 text-white font-medium py-2 px-6 rounded-full"
              >
                Try Again
              </button>
            </div>
            
            {/* Additional help text */}
            <p className="text-white/50 text-sm mt-4">
              If the problem persists, please try refreshing the page or check your internet connection.
            </p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div 
      className="category-page bg-page min-h-screen relative"
      style={category?.wallpaper ? {
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.8)), url(${category.wallpaper}/1920/1080)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      } : {}}
    >
      <Header />
      <div className="container mx-auto px-4 py-8 relative z-10">
        <div className="flex items-center mb-6">
          <button 
            onClick={handleBackClick}
            className="text-white flex items-center hover:text-accent transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
            Back
          </button>
        </div>
        
        {/* Enhanced category header with logo or text title */}
        <div className="mb-6">
          {category?.logo ? (
            <div className="category-page-logo-container mb-4">
              <img 
                src={`${category.logo}/600/100`}
                alt={category?.display_name || category?.name || 'Category'}
                className="category-page-logo h-16 md:h-20 lg:h-24 max-w-[400px] md:max-w-[500px] lg:max-w-[600px] object-contain"
                srcSet={`
                  ${category.logo}/300/50 300w,
                  ${category.logo}/420/70 420w,
                  ${category.logo}/540/90 540w,
                  ${category.logo}/600/100 600w
                `}
                sizes="(max-width: 767px) 300px, (max-width: 1023px) 420px, 600px"
                onError={(e) => {
                  // Fallback to text if logo fails to load
                  e.target.style.display = 'none';
                  e.target.nextElementSibling.style.display = 'block';
                }}
              />
              <h1 className="text-3xl font-bold text-white mb-2 hidden">
                {category?.display_name || category?.name || 'Category'}
              </h1>
            </div>
          ) : (
            <h1 className="text-3xl font-bold text-white mb-2">
              {category?.display_name || category?.name || 'Category'}
            </h1>
          )}
          {category?.description && (
            <p className="text-white/70 max-w-3xl">{category.description}</p>
          )}
        </div>
        
        <ErrorBoundary fallback={
          <div className="bg-red-900/30 border border-red-500 rounded-lg p-6 text-center">
            <h3 className="text-lg font-medium text-white mb-2">Error Loading Channels</h3>
            <p className="text-white/50 mb-4">There was a problem displaying the channels for this category.</p>
            <button 
              onClick={() => window.location.reload()}
              className="bg-white/10 hover:bg-white/20 text-white font-medium py-2 px-6 rounded-full"
            >
              Refresh Page
            </button>
          </div>
        }>
          <Suspense fallback={<LoadingSpinner message="Loading channels..." />}>
            {channels.length > 0 ? (
              <div className="category-channel-grid grid grid-cols-2 gap-4 px-1 sm:gap-5 md:grid-cols-3 md:gap-6 lg:grid-cols-4 xl:grid-cols-5">
                {channels.map((channel, index) => (
                  <div
                    key={channel?._id || index}
                    className="channel-card-container min-w-0"
                  >
                    <ChannelCard
                      channel={channel || {}}
                      style={category?.style}
                      index={index}
                      layout="grid"
                      onHoverModalShow={handleHoverModalShow}
                      onHoverModalHide={handleHoverModalHide}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-white/50 py-12">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-white/30 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" />
                </svg>
                <h3 className="text-lg font-medium text-white mb-2">No Channels Available</h3>
                <p className="text-white/50">This category doesn't have any channels yet.</p>
                <button 
                  onClick={retry}
                  className="mt-4 bg-white/10 hover:bg-white/20 text-white font-medium py-2 px-6 rounded-full"
                >
                  Refresh
                </button>
              </div>
            )}
          </Suspense>
        </ErrorBoundary>
      </div>
      
      {/* Integrated Hover Modal */}
      {hoverModalData.isVisible && (
        <ThumbnailHoverModal
          channel={hoverModalData.channel}
          isVisible={hoverModalData.isVisible}
          position={hoverModalData.position}
          onMouseEnter={handleModalMouseEnter}
          onMouseLeave={handleModalMouseLeave}
          onOpenPDPModal={handleOpenPDPModal}
        />
      )}
      
      {/* PDP Modal */}
      {pdpModalData.isVisible && (
        <PDPModal
          channel={pdpModalData.channel}
          isVisible={pdpModalData.isVisible}
          onClose={handleClosePDPModal}
          autoPlayVideo={pdpModalData.videoState.autoPlay}
          videoCurrentTime={pdpModalData.videoState.currentTime}
        />
      )}
      
      <Footer />
    </div>
  );
};

export default CategoryPage;

